import { Table } from 'reactstrap';
import { ISearchOrgResult } from '../../../types/org';
import { useState } from 'react';
import PaginationBar from '../../common/pagination-bar';
import OmsToolTip from '../../common/oms-tooltip/oms-tooltip';
import { Pageable } from '../../../types/common';
import TableHeaders, { TableHeaderInfo } from '../../common/table-header';
import Button from '../../common/button';
import { FaRegCircleXmark } from 'react-icons/fa6';
import { SearchOrgResultModel } from './search-org-result.model';

const PAGE_SIZE = 10;

interface SearchOrgResultProp extends Pageable<ISearchOrgResult> {
  selected: any | undefined;
  onOrgSelect: (item: ISearchOrgResult) => void;
  inputChanged: boolean;
  isInputError: boolean;
}

const TABLE_HEADERS: TableHeaderInfo<SearchOrgResultModel>[] = [
  { header: "Name", accessor: "orgName", sortable: true, hidden: false },
  { header: "Address", accessor: "normalizedAddress", sortable: true, hidden: false },
  { header: "Phone/Fax", accessor: "normalizedPhone", sortable: true, hidden: false, cssClass: "phone-column" },
];

// Private helper to render addresses for better accessibility
function RenderOrgAddresses({ orgAddressLines }: { orgAddressLines: Array<{ text: string; isExpired: boolean }> }): React.JSX.Element {
  if (!orgAddressLines || orgAddressLines.length === 0) return (<></>);

  if (orgAddressLines.length === 1) {
    if (orgAddressLines[0].isExpired) {
      return (<>{orgAddressLines[0].text} <FaRegCircleXmark title="Expired address" /></>);
    }
    return (<>{orgAddressLines[0].text}</>);
  }

  return (
    <ul className="undecorated">
      {orgAddressLines.map((line, i) => (
        <li key={`addr-${i}`}>{line.text}{line.isExpired && <FaRegCircleXmark title="Expired address" />}</li>
      ))}
    </ul>
  );
}

// Private helper to render phone numbers as text or list for better accessibility
function RenderPhones({ phones }: { phones: string[] }): React.JSX.Element {
  const items = (phones || []).filter(p => p && p.trim().length > 0);
  if (items.length === 0) return (<></>);
  if (items.length === 1) return (<>{items[0]}</>);
  return (
    <ul className="undecorated">
      {items.map((p, i) => (<li key={`phone-${i}`}>{p}</li>))}
    </ul>
  );
}

function SearchOrgResult(props: SearchOrgResultProp) {
  const { data: orgs, selected, onOrgSelect, inputChanged, isInputError } = props;
  const [sortedData, setSortedData] = useState<SearchOrgResultModel[]>([]);
  const [pagedData, setPagedData] = useState<SearchOrgResultModel[]>([]);
  const sortableSource: SearchOrgResultModel[] = (orgs || []).map(o => new SearchOrgResultModel(o));
  return (
    <>
      <Table id="orgs_table" striped className="fixed">
        <caption className="sr-only">Search results, sortable</caption>
        <TableHeaders headers={TABLE_HEADERS} sortableData={sortableSource} onSort={setSortedData} />

        <tbody>
          {sortedData.length>0
            ? pagedData.map((org:SearchOrgResultModel, index) => {
              return <tr key={org.id+'-'+index}>
                <td>
                  <Button color='link'
                    className={`oms-button-link ${selected === searchOrgSelectedOrg(org) ? 'oms-button-link-selected' : '' }`}
                    id={`${org.id}-${index}-title`}
                    onClick={()=>{onOrgSelect(org)}}>
                      <OmsToolTip orgId={org.id}>
                          {org.orgName}
                      </OmsToolTip>
                  </Button>
                </td>
                <td>
                  <RenderOrgAddresses orgAddressLines={org.addressLines.map(l => ({ text: l.text, isExpired: l.expired }))} />
                </td>
                <td>
                  <RenderPhones phones={org.normalizedPhone ? org.normalizedPhone.split('\n') : []} />
                </td>
              </tr>;
              })
            :<tr><td colSpan={TABLE_HEADERS.length} className="text-center">
              {inputChanged? (isInputError ? 'No results found.' : 'Search results will appear in the table.') :'No organization names were found with the exact search match.'}</td></tr>
          }
        </tbody>
      </Table>

      <PaginationBar data={sortedData} pageSize={PAGE_SIZE} onPageChange={setPagedData} />
    </>
  )
}

export function searchOrgSelectedOrg(record: ISearchOrgResult) : string {
  return `search-org-selected-org-${record.id}`;
}

export default SearchOrgResult
